/**
 * App-side wiring for `@heidi/commerce/data` (D2, ARCFE-972).
 *
 * Packages are variant-agnostic and store-free (G1/F5): this module injects
 * the app's shared QueryClient so commerce's module-scope cache commands hit
 * the SAME cache the app renders from, and the REACTIVE auth token (a hook,
 * so gated queries re-key on refresh; sourced from the already-store-coupled
 * `useIsAuthenticated` module per the Rematch containment ratchet). Transport is NOT injected here —
 * `data/api` reads the `@heidi/api/client` registry, which `scribeHTTP.ts`
 * populates at factory time (E5, ARCFE-805); the side-effect import below
 * guarantees that ordering. Imported at module scope in `_app.tsx` after the
 * workspace wiring, because commerce's hooks read workspace contracts.
 *
 * Ruling 65 adds the tier, billing and trial reads to it, mirroring
 * `useAuthToken`: an ACTION crosses as the app delegate itself, reactions
 * included; a VALUE crosses as a resolved value or a hook returning one, and
 * each member below names the app expression it mirrors.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import useReverseTrial from '@/hooks/useReverseTrial';
import { calculateTrialDaysLeft, calculateTrialHoursLeft } from '@/lib/utils';
import { isTauri, tauriStripeRedirectUrl } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { configureCommerceData } from '@heidi/commerce/data';

configureCommerceData({
  resolveStripeRedirectBase: () =>
    isTauri() ? tauriStripeRedirectUrl() : process.env.NEXT_PUBLIC_STRIPE_REDIRECT_URL!,
  useAuthToken,
  queryClient: reactQueryClient,
  // Trial countdown math — stays app-side because it bottoms out in
  // `parseTimeStamp`, which reads the account timezone (D3, ARCFE-977).
  trialCalculators: { calculateTrialDaysLeft, calculateTrialHoursLeft },
  // Ruling 65. All three cross by reference, never wrapped: a wrapper would be
  // a second binding to keep in step, and it would drop `useReverseTrial`'s
  // memoised identity. The three copy strings are absent from the seam's type.
  useUserTier: useQueryUserTier,
  useUserBillingInfo: useQueryUserBillingInfo,
  useReverseTrial,
});
